import { Typography, Box, Button, Toolbar, TextField as MuiTextField, Alert, Collapse, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getIntegrationConfig, Integration } from '../../../types/integrations';
import CsvConfigEditor from './CsvConfigEditor';
import SapsfConfigEditor from './SapsfConfigEditor';
import { Confirm, SaveButton, SimpleForm, useDataProvider, useEditController, useNotify, useRedirect, useRefresh } from 'react-admin';
import { useState } from 'react';

type ConfigEditorProps = {
  integration: Integration;
};

export const ALLOWED_CONFIG_EDITORS = ['ricardo.prezotto@oitchau.com.br', 'thalia.cavalcante@oitchau.com.br', 'diego.siebra@oitchau.com.br'];

const CustomToolbar = ({ show = true }: { show?: boolean }) => <Toolbar>{show && <SaveButton />}</Toolbar>;

export const VENDORS_WITH_CONFIG_PANEL = ['CSV', 'SAPSF'];

export function RawJsonConfigEditor({ integration, canEdit }: { integration: Integration; canEdit: boolean }) {
  const [jsonText, setJsonText] = useState(JSON.stringify(getIntegrationConfig(integration) || {}, null, 2));
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const dataProvider = useDataProvider();
  const controllerProps = useEditController();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonText(e.target.value);
    try {
      JSON.parse(e.target.value);
      setJsonError(null);
    } catch {
      setJsonError('Invalid JSON');
    }
  };

  const handleSave = () => {
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    setSaveError(null);
    if (controllerProps.record) {
      try {
        const { data: updated } = await dataProvider.update('integrations_config', {
          id: controllerProps.record.id,
          data: { integrationConfig: JSON.parse(jsonText) },
          previousData: controllerProps.record
        });
        refresh();
        notify('Configuration updated successfully', { type: 'success' });
        redirect('show', 'integrations', controllerProps.record.id, updated);
      } catch (error: any) {
        const message = error?.body?.message || error?.message || 'An unexpected error occurred while updating the configuration.';
        setSaveError(message);
        notify('Error updating configuration', { type: 'error' });
      }
    }
  };

  return (
    <Box>
      <Collapse in={!!saveError}>
        <Alert
          severity="error"
          sx={{ mb: 2, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem' }}
          action={
            <IconButton size="small" onClick={() => setSaveError(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {saveError}
        </Alert>
      </Collapse>
      <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
        Configuration ({integration.vendor} / {integration.source})
      </Typography>
      <MuiTextField multiline fullWidth minRows={15} maxRows={30} value={jsonText} onChange={handleChange} error={!!jsonError} helperText={jsonError} disabled={!canEdit} InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.85rem' } }} />
      {canEdit && (
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={!!jsonError}>
            Save Configuration
          </Button>
        </Box>
      )}
      <Confirm isOpen={confirmOpen} title="Confirm Save Configuration" content="Are you sure you want to save these configuration changes?" onConfirm={handleConfirm} onClose={() => setConfirmOpen(false)} />
    </Box>
  );
}

function ConfigEditor({ integration }: ConfigEditorProps) {
  const [configFormData, setConfigFormData] = useState<any>(null);
  const [configConfirmOpen, setConfigConfirmOpen] = useState(false);
  const [configSaveError, setConfigSaveError] = useState<string | null>(null);
  const dataProvider = useDataProvider();
  const controllerProps = useEditController();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const userEmail = JSON.parse(localStorage.getItem('GoogleAccountInfo') || '{}').email || '';
  const canEditConfig = ALLOWED_CONFIG_EDITORS.includes(userEmail);

  let ConfigDetails = null;

  switch (integration.vendor) {
    case 'CSV':
      ConfigDetails = CsvConfigEditor;
      break;
    case 'SAPSF':
      ConfigDetails = SapsfConfigEditor;
      break;
    default:
      return <RawJsonConfigEditor integration={integration} canEdit={canEditConfig} />;
  }

  const handleConfigSubmit = (data: any) => {
    setConfigFormData(data);
    setConfigConfirmOpen(true);
    return Promise.resolve();
  };

  const handleConfigConfirm = async () => {
    setConfigConfirmOpen(false);
    setConfigSaveError(null);

    if (configFormData && controllerProps.record) {
      try {
        const { data: updated } = await dataProvider.update('integrations_config', {
          id: controllerProps.record.id,
          data: { integrationConfig: configFormData.config },
          previousData: controllerProps.record
        });

        refresh();
        notify('Configuration updated successfully', { type: 'success' });
        redirect('show', 'integrations', controllerProps.record.id, updated);
      } catch (error: any) {
        const message = error?.body?.message || error?.message || 'An unexpected error occurred while updating the configuration.';
        setConfigSaveError(message);
        notify('Error updating configuration', { type: 'error' });
      }
    }
  };

  const handleConfigCancel = () => {
    setConfigConfirmOpen(false);
    setConfigFormData(null);
  };

  return (
    <SimpleForm record={{ config: getIntegrationConfig(integration) }} onSubmit={handleConfigSubmit} toolbar={<CustomToolbar show={canEditConfig} />}>
      <Collapse in={!!configSaveError}>
        <Alert
          severity="error"
          sx={{ mb: 2, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem' }}
          action={
            <IconButton size="small" onClick={() => setConfigSaveError(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {configSaveError}
        </Alert>
      </Collapse>
      <Box>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          Configuration ({integration.vendor} / {integration.source})
        </Typography>
        <ConfigDetails />
      </Box>
      <Confirm isOpen={configConfirmOpen} title="Confirm Save Configuration" content="Are you sure you want to save these configuration changes?" onConfirm={handleConfigConfirm} onClose={handleConfigCancel} />
    </SimpleForm>
  );
}

export default ConfigEditor;
